// ---------------------------------------------------------------------------
// useResolveDataset — when a saved template feeds a `dataSetId` in via props
// (view/edit mode), the live search results usually don't include that dataset
// until the user types a matching query. This resolves it directly by id so
// the Combobox can show the real name + version, seeds the search box with
// that name (unless the user has already typed), marks the id as validated
// (so the "missing" warning stays quiet), and exposes the resolved version and
// any resolve error. Each id resolves once per mount via a ref.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';

import type { DataHubService } from '../../../../../services/dataHubService.js';

import { renderOptionText } from './datasetOption.js';

export function useResolveDataset({
  selectedId,
  dataHubService,
  onSeedQuery,
  onValidatedIdChange,
}: {
  selectedId: string | null;
  dataHubService: DataHubService;
  onSeedQuery: (name: string) => void;
  onValidatedIdChange: (id: string | null) => void;
}): { resolveError: string | null; resolvedVersion: number | null } {
  const resolvedIdRef = useRef<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolvedVersion, setResolvedVersion] = useState<number | null>(null);

  useEffect(() => {
    if (selectedId === null) {
      resolvedIdRef.current = null;
      setResolveError(null);
      setResolvedVersion(null);
      return;
    }
    if (resolvedIdRef.current === selectedId) return;
    resolvedIdRef.current = selectedId;
    setResolveError(null);
    let cancelled = false;
    dataHubService
      .getDataset(selectedId)
      .then((ds) => {
        if (cancelled) return;
        onSeedQuery(renderOptionText(ds));
        onValidatedIdChange(selectedId);
        setResolvedVersion(ds.version);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Failed to resolve dataset.';
        // Surface this so the user can tell whether the dataset is
        // missing, they're unauthorized, or the request failed. Otherwise
        // the picker just looks broken with an empty Combobox.
        console.warn(
          '[ExistingDataSetPicker] failed to resolve dataset',
          selectedId,
          err,
        );
        setResolveError(message);
      });
    return () => {
      cancelled = true;
    };
    // onSeedQuery/onValidatedIdChange are stable setters from the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, dataHubService]);

  return { resolveError, resolvedVersion };
}
